import { AppShell, buttonVariants, cn, SheetClose } from '@napaicp/ui'
import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom'
import napaicpLogo from '@/assets/napaicp-logo.png'
import { AccountMenu } from '@/components/common/account-menu'
import { ForcedPasswordChangeScreen } from '@/components/common/forced-password-change-screen'
import { LanguageSwitcher } from '@/components/common/language-switcher'
import { RoleSwitchAnnouncer } from '@/components/common/role-switch-announcer'
import { SiteFooter } from '@/components/common/site-footer'
import { ThemeToggle } from '@/components/common/theme-toggle'
import { CompetitionSwitcher } from '@/features/competitions/competition-switcher'
import { RoleSwitcher } from '@/features/competitions/role-switcher'
import { useSession } from '@/features/session/use-session'
import { isSupportedLocale, persistLocale } from '@/lib/i18n'
import { hasAnyPlatformReadAccess } from '@/lib/platform-access'
import { useDocumentTitle } from '@/lib/use-document-title'

/**
 * UX-039/BR-095 (Wave U Phase 1): the Competition Switcher is only
 * meaningful on a screen scoped to one specific competition — showing it on
 * the home page, the public competition list, or an account page that has
 * nothing to do with any one competition just invites a confusing
 * "選擇競賽" control with no obvious effect. Whitelisting the competition-
 * scoped routes (rather than blacklisting the unrelated ones) means a new
 * unrelated route defaults to *not* showing it, which is the safer default.
 */
function isCompetitionScopedPath(pathname: string): boolean {
  return (
    /^\/admin\/competitions\/[^/]+/.test(pathname) ||
    /^\/student\/competitions\/[^/]+/.test(pathname) ||
    /^\/judge(\/|$)/.test(pathname)
  )
}

export function RootLayout() {
  const { t, i18n } = useTranslation(['common', 'nav', 'platform', 'account'])
  const location = useLocation()
  const session = useSession()
  useDocumentTitle()

  const signedIn = session.data?.authenticated === true
  // BR-082/API-050/DM-028: a session minted from a Judge Temporary Initial
  // Password is restricted server-side until this completes — every
  // protected API call the real page content would make fails with
  // `PASSWORD_CHANGE_REQUIRED`. Gating here, above every route's own
  // content, means no individual page has to know or care about this state;
  // the header (including sign-out) stays reachable since only `<Outlet />`
  // is swapped out below.
  const passwordChangeRequired = signedIn && session.data?.user?.passwordChangeRequired === true
  const showCompetitionSwitcher =
    signedIn && !passwordChangeRequired && isCompetitionScopedPath(location.pathname)
  const userLocale = session.data?.authenticated === true ? session.data.user?.locale : undefined
  // UX-009: the Platform Administration nav entry only renders for an
  // account holding at least one `platform.*.read` permission — never
  // derived from a raw `globalRole === 'super_admin'` string check. An
  // account without platform read access must see nothing here that hints
  // the capability exists at all.
  const showPlatformNav =
    signedIn && hasAnyPlatformReadAccess(session.data?.user?.globalRole ?? 'user')

  // The user's stored profile locale only takes effect once, right after we
  // learn who is signed in, and only when the switcher has not already been
  // used on this device — an explicit local choice always wins.
  const appliedProfileLocale = useRef(false)
  useEffect(() => {
    if (appliedProfileLocale.current) {
      return
    }
    if (typeof window !== 'undefined' && window.localStorage.getItem('napaicp.locale')) {
      appliedProfileLocale.current = true
      return
    }
    if (isSupportedLocale(userLocale)) {
      appliedProfileLocale.current = true
      persistLocale(userLocale)
      void i18n.changeLanguage(userLocale)
    }
  }, [userLocale, i18n])

  const competitionsNavLinkClassName = ({ isActive }: { isActive: boolean }) =>
    cn(
      'text-sm transition-colors',
      isActive ? 'font-medium' : 'text-muted-foreground hover:text-foreground',
    )
  const utilityNavLinkClassName =
    'text-sm text-muted-foreground transition-colors hover:text-foreground'

  // PR-048/BR-099..102 (TASK-U-001b, v3.1.1 correction): "競賽" has no
  // dedicated browse route yet (PR-052's search/filterable list is a later
  // Wave U task) — the public "currently open competitions" content already
  // lives inline on `/`, so the nav item points at that section via anchor
  // rather than inventing a page. A plain `Link` (not `NavLink`) is used
  // deliberately: `NavLink` matches by pathname only (ignoring the hash),
  // which would make it report "active" at the exact same time as "首頁" and
  // apply the same active styling to both — misleading for two distinct nav
  // entries that happen to share a route.
  // PR-053 (v3.1.2, TASK-U-009): `/` is now auth-independent (no longer
  // redirects a signed-in visitor elsewhere), so this href no longer needs
  // to branch on `signedIn` the way it used to.
  const competitionsHref = '/#published-competitions'

  // BUG-20260821-001: `Link to={competitionsHref}` only changes the URL hash
  // when the visitor is already on `/` or `/home` — React Router never
  // remounts `Outlet` for a hash-only change, and the browser's native
  // anchor-scroll-on-load doesn't apply either (that only fires for a fresh
  // full navigation). Without this, clicking "競賽" from the same page did
  // nothing visible. The target section can also mount a tick after this
  // effect runs (e.g. `/home` gates its first render on the session query),
  // so retry across animation frames instead of a single lookup.
  //
  // BUG-20260822-001: navigating to a *different* route with no hash (e.g.
  // clicking 系統介紹/使用說明/技術架構 while scrolled down on the current
  // page) used to leave the browser at whatever scroll offset it already
  // had — React Router's client-side navigation never resets scroll the
  // way a full page load does. Every public page must open at its own top,
  // not wherever the previous page happened to be scrolled to.
  useEffect(() => {
    const id = location.hash.slice(1)
    if (!id) {
      window.scrollTo(0, 0)
      return
    }

    let cancelled = false
    let frame = 0
    let attempts = 0

    const tryScroll = () => {
      if (cancelled) return
      const el = document.getElementById(id)
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' })
        return
      }
      attempts += 1
      if (attempts < 60) {
        frame = requestAnimationFrame(tryScroll)
      }
    }

    tryScroll()

    return () => {
      cancelled = true
      cancelAnimationFrame(frame)
    }
  }, [location.pathname, location.hash])

  // BUG-20260821-002: clicking "首頁" while already on the home page (same
  // pathname) fires no navigation at all — React Router never emits a new
  // `location`, so nothing re-renders and no scroll happens, which reads as
  // "the button does nothing" once the page has been scrolled down. A plain
  // click handler on the link sidesteps depending on a location change.
  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' })

  return (
    <AppShell
      brand={
        // UX-089 (v3.26): the official Logo keeps its aspect ratio and is
        // never CSS-recolored (BRAND-004/BRAND-006, unchanged) — only the
        // wordmark's *container* gets `min-width: 0` + truncation so a
        // narrow viewport shrinks the text instead of pushing the Account
        // trigger / hamburger out of the header. The `siteUrl` sub-line is
        // hidden below the `lg` breakpoint where the header is already in
        // compact mode — it's not part of what a mobile visitor needs to
        // identify the brand, and every pixel it took was pixels the
        // Account/Menu cluster couldn't have.
        <Link to="/" className="flex min-w-0 items-center gap-2">
          <img src={napaicpLogo} alt="" className="size-9 shrink-0" />
          <span className="flex min-w-0 flex-col leading-tight">
            <span className="truncate">{t('common:headerBrand')}</span>
            <span className="hidden truncate text-[10px] font-normal text-muted-foreground lg:block">
              {t('common:siteUrl')}
            </span>
          </span>
        </Link>
      }
      mobileMenuTitle={t('nav:menu')}
      mobileMenuLabel={t('nav:openMenu')}
      mobileMenuCloseLabel={t('common:action.close')}
      skipToContentLabel={t('common:action.skipToContent')}
      // UX-048 (Wave U Phase 3): Mobile Header 僅保留 Logo、Menu、Account —
      // the persistent (non-drawer) mobile row shows only the account entry
      // point (the same `AccountMenu` used at desktop width, or a sign-in
      // link) next to the hamburger trigger. Theme/language toggles are
      // lower-frequency and now live inside the drawer (`mobileMenu` below)
      // instead of occupying an always-visible header slot.
      mobileActions={
        signedIn ? (
          <AccountMenu compact />
        ) : (
          <Link className={buttonVariants({ size: 'sm' })} to="/login">
            {t('common:action.signIn')}
          </Link>
        )
      }
      headerEnd={
        <>
          {/* UX-039/UX-051/PR-048 (v3.1.1, TASK-U-001b; v3.1.2, TASK-U-009;
              v3.1.4 revision below; v3.23 revision below that): Primary
              Navigation is its own landmark, distinguished from Utility
              Navigation by accessible name (not just layout position).
              Signed-out: 首頁/探索競賽/系統介紹/使用說明 (BR-099/AC-073).
              Signed-in: 首頁/總覽/探索競賽/我的競賽/平台管理 — high-frequency
              application tasks only. "首頁" always targets `/` regardless of
              auth state (PR-053) — "總覽" is the separate, signed-in-only
              item that carries the personalized Dashboard content "首頁"
              used to.
              v3.23 (P0 student-workspace UX polish): 申請主辦 moved out of
              this landmark into the Account Menu (a rare, deliberate action,
              not a routine one every signed-in visitor needs one click
              away) — see `account-menu.tsx`. 系統介紹/使用說明 dropped from
              the signed-in Utility landmark below too, since `SiteFooter`
              already carries both on every page and repeating them in the
              header just added to the "management console" feel the user
              asked to reduce; the original BR-100/BR-107/PR-113 rationale
              for keeping a header copy (footer-only 系統介紹 reading as "it
              disappeared after signing in") no longer applies once the
              footer is consistently present on every signed-in screen too. */}
          <nav aria-label={t('nav:primary')} className="flex items-center gap-3">
            <NavLink to="/" className={competitionsNavLinkClassName} onClick={scrollToTop} end>
              {t('nav:home')}
            </NavLink>
            {signedIn ? (
              <NavLink to="/home" className={competitionsNavLinkClassName} end>
                {t('nav:overview')}
              </NavLink>
            ) : null}
            <Link to={competitionsHref} className={utilityNavLinkClassName}>
              {t('nav:competitions')}
            </Link>
            <NavLink to="/events" className={competitionsNavLinkClassName}>
              {t('nav:events')}
            </NavLink>
            {signedIn ? (
              <NavLink to="/admin/competitions" className={competitionsNavLinkClassName}>
                {t('nav:myCompetitions')}
              </NavLink>
            ) : (
              <>
                <NavLink to="/about" className={competitionsNavLinkClassName}>
                  {t('nav:about')}
                </NavLink>
                <NavLink to="/howto" className={competitionsNavLinkClassName}>
                  {t('nav:howto')}
                </NavLink>
                <NavLink to="/help" className={competitionsNavLinkClassName}>
                  {t('nav:helpCenter')}
                </NavLink>
              </>
            )}
            {showPlatformNav ? (
              <NavLink to="/platform" className={competitionsNavLinkClassName}>
                {t('platform:nav.linkLabel')}
              </NavLink>
            ) : null}
          </nav>

          {/* Utility Navigation landmark (UX-039/UX-051, v3.1.4 revision;
              v3.23 revision below): language, theme, and the in-context
              Competition Switcher for a signed-in visitor. 系統介紹/使用說明
              are no longer duplicated here for signed-in visitors (v3.23,
              see the Primary Navigation comment above) — `SiteFooter`
              already carries both on every page. */}
          <nav aria-label={t('nav:utility')} className="flex items-center gap-3">
            {showCompetitionSwitcher ? (
              <>
                <CompetitionSwitcher />
                <RoleSwitcher />
              </>
            ) : null}
            <ThemeToggle />
            <LanguageSwitcher />
          </nav>

          {signedIn ? (
            <AccountMenu />
          ) : (
            <Link className={buttonVariants({ size: 'sm' })} to="/login">
              {t('common:action.signIn')}
            </Link>
          )}
        </>
      }
      mobileMenu={
        <>
          {signedIn ? (
            <div className="flex flex-col gap-4">
              {/* AC-077: the drawer must expose the same essentials as
                  desktop, grouped for hierarchy rather than one flat list
                  (source doc §9.2 — 主要功能/協助/偏好; 帳號 is intentionally
                  omitted below, see the UX-048 note further down). */}
              <nav aria-label={t('nav:groups.primary')} className="flex flex-col gap-3">
                <SheetClose asChild>
                  <NavLink
                    to="/"
                    className={competitionsNavLinkClassName}
                    onClick={scrollToTop}
                    end
                  >
                    {t('nav:home')}
                  </NavLink>
                </SheetClose>
                <SheetClose asChild>
                  <NavLink to="/home" className={competitionsNavLinkClassName} end>
                    {t('nav:overview')}
                  </NavLink>
                </SheetClose>
                <SheetClose asChild>
                  <Link to={competitionsHref} className={utilityNavLinkClassName}>
                    {t('nav:competitions')}
                  </Link>
                </SheetClose>
                <SheetClose asChild>
                  <NavLink to="/events" className={competitionsNavLinkClassName}>
                    {t('nav:events')}
                  </NavLink>
                </SheetClose>
                <SheetClose asChild>
                  <NavLink to="/admin/competitions" className={competitionsNavLinkClassName}>
                    {t('nav:myCompetitions')}
                  </NavLink>
                </SheetClose>
                {showCompetitionSwitcher ? (
                  <>
                    <CompetitionSwitcher />
                    <RoleSwitcher />
                  </>
                ) : null}
                {showPlatformNav ? (
                  <SheetClose asChild>
                    <NavLink to="/platform" className={competitionsNavLinkClassName}>
                      {t('platform:nav.linkLabel')}
                    </NavLink>
                  </SheetClose>
                ) : null}
              </nav>
              {/* BR-111/UX-091 (v3.26): the v3.23 rationale for dropping 系統
                  介紹/使用說明 from this drawer — "SiteFooter already carries
                  both on every page" — assumed a signed-in visitor would
                  scroll the page to reach the footer. v3.26 makes explicit
                  that Mobile navigation must stay functionally equivalent to
                  Desktop (which keeps neither in its own nav anymore either,
                  but is one hamburger tap + one footer scroll away at a
                  wide viewport where the footer is far less of a reach) —
                  the acceptance criterion (AC-138) needs both reachable
                  directly from the Drawer without depending on how far the
                  underlying page happens to scroll. 申請主辦 stays out of
                  the drawer (unchanged): it remains in the Account Menu as
                  "申請成為競賽主辦方". */}
              <nav
                aria-label={t('nav:groups.help')}
                className="flex flex-col gap-3 border-t border-border pt-3"
              >
                <SheetClose asChild>
                  <NavLink to="/about" className={competitionsNavLinkClassName}>
                    {t('nav:about')}
                  </NavLink>
                </SheetClose>
                <SheetClose asChild>
                  <NavLink to="/howto" className={competitionsNavLinkClassName}>
                    {t('nav:howto')}
                  </NavLink>
                </SheetClose>
                <SheetClose asChild>
                  <NavLink to="/help" className={competitionsNavLinkClassName}>
                    {t('nav:helpCenter')}
                  </NavLink>
                </SheetClose>
              </nav>
              {/* UX-048 (Wave U Phase 3, unchanged here): theme/language stay
                  in the drawer rather than the persistent mobile header row.
                  Profile/Security/Roles/Sign-out are intentionally not
                  repeated here — the `AccountMenu` already visible in the
                  mobile header provides all five, and duplicating them here
                  would just be a second, redundant path to the same
                  destinations. */}
              <div
                aria-label={t('nav:groups.preferences')}
                className="flex items-center gap-2 border-t border-border pt-3"
              >
                <ThemeToggle />
                <LanguageSwitcher />
              </div>
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              {/* 探索 (Explore) — signed-out equivalence for BR-099/AC-073/
                  AC-077: 首頁/競賽/系統介紹/使用說明 all reachable from the
                  drawer, matching the desktop primary nav one-for-one. */}
              <nav aria-label={t('nav:groups.explore')} className="flex flex-col gap-3">
                <SheetClose asChild>
                  <NavLink
                    to="/"
                    className={competitionsNavLinkClassName}
                    onClick={scrollToTop}
                    end
                  >
                    {t('nav:home')}
                  </NavLink>
                </SheetClose>
                <SheetClose asChild>
                  <Link to={competitionsHref} className={utilityNavLinkClassName}>
                    {t('nav:competitions')}
                  </Link>
                </SheetClose>
                <SheetClose asChild>
                  <NavLink to="/events" className={competitionsNavLinkClassName}>
                    {t('nav:events')}
                  </NavLink>
                </SheetClose>
                <SheetClose asChild>
                  <NavLink to="/about" className={competitionsNavLinkClassName}>
                    {t('nav:about')}
                  </NavLink>
                </SheetClose>
                <SheetClose asChild>
                  <NavLink to="/howto" className={competitionsNavLinkClassName}>
                    {t('nav:howto')}
                  </NavLink>
                </SheetClose>
                <SheetClose asChild>
                  <NavLink to="/help" className={competitionsNavLinkClassName}>
                    {t('nav:helpCenter')}
                  </NavLink>
                </SheetClose>
              </nav>
              <div
                aria-label={t('nav:groups.preferences')}
                className="flex items-center gap-2 border-t border-border pt-3"
              >
                <ThemeToggle />
                <LanguageSwitcher />
              </div>
              <SheetClose asChild>
                <Link className={cn(buttonVariants({ size: 'sm' }), 'w-full')} to="/login">
                  {t('common:action.signIn')}
                </Link>
              </SheetClose>
            </div>
          )}
        </>
      }
      footer={<SiteFooter />}
    >
      {/* UX-269: one shared live region for every Role Switch, anywhere in
          the app — mounted here (not inside `RoleSwitcher` itself, which
          renders twice, once for desktop and once inside the mobile Sheet)
          so it survives the navigation the switch performs. */}
      <RoleSwitchAnnouncer />
      {passwordChangeRequired ? <ForcedPasswordChangeScreen /> : <Outlet />}
    </AppShell>
  )
}
